/**
 * Building a `ResegmentOp` from the APPROVED constants.
 *
 * The thresholds are not ours. `ResegmentOp` embeds `LineBreakOptions`, whose fields are shared
 * with CLEAN's `ReflowOp`, and CLEAN escalated the whole set as ONE decision precisely so a
 * founder is not asked to approve two halves of one shared value. So this composes
 * `RESEGMENT_DEFAULTS`, `LINE_BREAK_DEFAULTS` and `LINE_BREAK_DEFAULTS_CJK` rather than naming
 * a single number of its own.
 *
 * `honourSegmentBoundaries` is FALSE and that is not a tunable default in the ordinary sense —
 * it is the component's premise. Whisper's boundaries are model artefacts, and honouring them
 * ships the exact bug re-segmentation exists to prevent.
 */

import type { LineBreakOptions, ResegmentOp, TrackId } from '../../contracts/cue-model.ts';
import {
  LINE_BREAK_DEFAULTS,
  LINE_BREAK_DEFAULTS_CJK,
  profileForLanguage,
  RESEGMENT_DEFAULTS,
} from '../transforms/clean/defaults.ts';

/**
 * The line-break options a track's language implies — **cell budget from the language's own QC
 * profile**, not from one constant for all of CJK.
 *
 * This used to test `primary === 'zh' || 'ja' || 'ko' || 'yue'` and hand back
 * `LINE_BREAK_DEFAULTS_CJK`, whose `maxCells` is 32. But CJK is not one budget:
 *
 *     PROFILE_CHINESE 32 · PROFILE_KOREAN 32 · PROFILE_JAPANESE 26
 *
 * and 26 is cited — CLEAN's SOURCES block records **N-JPN: 13 full-width characters per line**,
 * which is 26 cells on the full-width-is-2 scale. So a Japanese track was BROKEN at 32 cells and
 * JUDGED at 26 by `profileForLanguage`, the selector `src/qc/qc.ts:103` already uses. The same defect
 * as `maxCps`, one field over, and **my own test asserted the wrong side of it** —
 * `breakOptionsFor('ja').maxCells === 32` pinned the divergence in place.
 *
 * Reading the profile also deletes the hardcoded language list, which was a second copy of
 * `PROFILE_BY_LANGUAGE` that had to agree with it and silently did not.
 */
export function breakOptionsFor(language: string | null): LineBreakOptions {
  const profile = profileForLanguage(language);
  if (profile === null) return LINE_BREAK_DEFAULTS;
  // The CJK base carries the `cjk` width model and clause breakers; only the budget is per-language.
  return { ...LINE_BREAK_DEFAULTS_CJK, maxCells: profile.maxCells };
}

/**
 * The reading-speed ceiling a track's language implies — **CLEAN's own QC number, not a new one.**
 *
 * `RESEGMENT_DEFAULTS.maxCps` is 20, which its own comment marks as *"N-ENG adult, AMZN"*. Building
 * every language to an English ceiling meant a Japanese track was SPLIT at a 20 CPS budget and then
 * JUDGED by `src/qc/qc.ts` at 9, through `profileForLanguage` — the same selector used here. Two
 * halves of one system disagreeing by more than 2×, with the user on the receiving end reading CJK
 * cues that flash past.
 *
 *     profileForLanguage:   zh 11 · ja 9 · ko 14 · everything else, no opinion → 20
 *
 * `breakOptionsFor` below has always selected the CJK CELL budget per language. This function is the
 * same idea applied to the field it was missing, so the builder is language-aware in both dimensions
 * or neither. Raised as an unverified lead by the CTO's research pass in S22; confirmed here against
 * CLEAN's constants and `qc.ts:103`.
 */
export function maxCpsFor(language: string | null): number {
  return profileForLanguage(language)?.maxCps ?? RESEGMENT_DEFAULTS.maxCps;
}

export interface ResegmentOverrides {
  readonly break?: Partial<LineBreakOptions>;
  readonly minDuration?: ResegmentOp['minDuration'];
  readonly maxDuration?: ResegmentOp['maxDuration'];
  readonly maxCps?: number;
  readonly minGap?: ResegmentOp['minGap'];
  readonly silenceSplit?: ResegmentOp['silenceSplit'];
  readonly honourSegmentBoundaries?: boolean;
  readonly breakOnSpeakerChange?: boolean;
  readonly classifyKinds?: boolean;
  readonly padStart?: ResegmentOp['padStart'];
  readonly padEnd?: ResegmentOp['padEnd'];
  readonly emitWordRuns?: boolean;
  readonly dropAboveNoSpeechProb?: number | null;
}

/**
 * A complete `ResegmentOp` for a track, with every threshold from the approved set.
 *
 * `dropAboveNoSpeechProb` DEFAULTS TO `null` — the filter off — and that changed in S22 on evidence
 * from faster-whisper's own source, which is installed in this repo at
 * `.venv/Lib/site-packages/faster_whisper/transcribe.py`:
 *
 *     should_skip = result.no_speech_prob > options.no_speech_threshold        # 1217
 *     if (log_prob_threshold is not None and avg_logprob > log_prob_threshold):
 *         # don't skip if the logprob is high enough, despite the no_speech_prob
 *         should_skip = False                                                 # 1224
 *
 * and its docstring: *"no_speech_threshold: If the no_speech probability is higher than this value
 * **AND** the average log probability … is below `log_prob_threshold`, consider the segment silent."*
 * Defaults `no_speech_threshold = 0.6`, `log_prob_threshold = -1.0`.
 *
 * **The model's skip is a CONJUNCTION.** So every segment that reaches our wire with
 * `noSpeechProb > 0.6` is one where `avgLogprob > -1.0` — the model looked at exactly this pair and
 * DECLINED to drop it, because it was confident about the words. A default of 0.6 dropped precisely
 * the segments the model kept on purpose: real speech, deleted silently, with no way for a user to
 * know what went.
 *
 * THE "ONE OF THREE" FRAMING WAS WRONG — corrected in S24 after CORE read the rest of the file, and
 * kept here rather than deleted because the wrong version is the one a reader reconstructs. The
 * contract described a DISJUNCTION (`avgLogprob < -1.0 || compressionRatio > 2.4 || noSpeechProb >
 * 0.6`) and I repeated it as "three conditions, we expose one". At the source they are **three
 * mechanisms with three different jobs**:
 *
 *   DISCARD, never emitted     :1216-1224   no_speech_prob > 0.6 AND NOT (avg_logprob > -1.0)
 *   RETRY HOTTER, discards nothing :1481-1498   compression_ratio > 2.4 → needs_fallback = True
 *                                               avg_logprob < -1.0      → needs_fallback = True
 *   STOP RETRYING, accept silence  :1507-1513   the same conjunction, used to PREVENT a fallback
 *
 * `compressionRatio` never discards anything, and `avgLogprob` appears in two of the three — which is
 * what made flattening them into one `||` tempting. **The rule that reaches us is the DISCARD, and it
 * is a two-field conjunction; this op exposes one of those two.** That is the reason the default is
 * off, and it is a smaller and more defensible reason than the one it replaces.
 *
 * A caller who wants the old behaviour passes `{ dropAboveNoSpeechProb: 0.6 }`.
 */
export function resegmentOp(
  trackId: TrackId,
  language: string | null = null,
  over: ResegmentOverrides = {},
): ResegmentOp {
  const base = breakOptionsFor(language);
  return {
    op: 'resegment',
    trackId,
    break: { ...base, ...over.break, cjk: over.break?.cjk ?? base.cjk },
    minDuration: over.minDuration ?? RESEGMENT_DEFAULTS.minDurationTicks,
    maxDuration: over.maxDuration ?? RESEGMENT_DEFAULTS.maxDurationTicks,
    maxCps: over.maxCps ?? maxCpsFor(language),
    minGap: over.minGap ?? RESEGMENT_DEFAULTS.minGapTicks,
    silenceSplit: over.silenceSplit ?? RESEGMENT_DEFAULTS.silenceSplitTicks,
    // FALSE. The premise, not a preference.
    honourSegmentBoundaries: over.honourSegmentBoundaries ?? false,
    breakOnSpeakerChange: over.breakOnSpeakerChange ?? true,
    classifyKinds: over.classifyKinds ?? true,
    padStart: over.padStart ?? RESEGMENT_DEFAULTS.padStartTicks,
    padEnd: over.padEnd ?? RESEGMENT_DEFAULTS.padEndTicks,
    emitWordRuns: over.emitWordRuns ?? true,
    // `??` would be wrong here: `null` is a MEANINGFUL value (turn the filter off) and a `??` chain
    // silently turns it back on. The presence of the key is the signal, not its truthiness — which
    // still matters now that OFF is the default, because a caller may pass 0.6 deliberately.
    dropAboveNoSpeechProb:
      'dropAboveNoSpeechProb' in over ? (over.dropAboveNoSpeechProb ?? null) : null,
  };
}

export { RESEGMENT_DEFAULTS };
